import { useEffect, useMemo, useState } from "react";
import {
	MRT_ActionMenuItem,
	MRT_ShowHideColumnsButton,
	MRT_ToggleFiltersButton,
	MRT_ToggleGlobalFilterButton,
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import { DescriptionOutlined, VisibilityOutlined } from "@mui/icons-material";

import "./VesselRevisionGridTable.scss";

import { getQueryData } from "../../../services/utilities";
import { convertLocaltoUTCFormat } from "../../../services/DateFormatUtility";

const VesselRevisionGridTable = ({ hasRevisionData }) => {
	const [vesselRevisionData, setVesselRevisionData] = useState([]);
	const [vesselRevisionRowSelection, setVesselRevisionRowSelection] =
		useState({});
	const [vesselRevisionColumnFilters, setVesselRevisionColumnFilters] =
		useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [vesselRevisionGlobalFilter, setVesselRevisionGlobalFilter] =
		useState("");
	const [vesselRevisionSort, setVesselRevisionSort] = useState([]);
	const [loading, setLoading] = useState(false);
	const [vesselRevisionPagination, setVesselRevisionPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});
	const [selectAll, setSelectAll] = useState(false);

	useEffect(() => {
		let sortOptions = vesselRevisionSort.map((x) => ({
			SortField: x.id,
			IsAscending: !x.desc,
		}));

		if (vesselRevisionColumnFilters.length > 0) {
			filterVessel(
				vesselRevisionColumnFilters,
				vesselRevisionPagination.pageIndex,
				vesselRevisionPagination.pageSize,
				sortOptions
			);
		} else {
			searchVessels(
				vesselRevisionGlobalFilter,
				vesselRevisionPagination.pageIndex,
				vesselRevisionPagination.pageSize,
				sortOptions
			);
		}
	}, [
		vesselRevisionGlobalFilter,
		vesselRevisionPagination.pageIndex,
		vesselRevisionPagination.pageSize,
		vesselRevisionColumnFilters,
		vesselRevisionSort,
	]);

	const searchVessels = (
		searchStr = "",
		pageIndex = 0,
		pageSize = 25,
		sortOptions
	) => {
		let requestPayload = {
			entity: "SEEMPIMODTL",
			query: {
				conditions: [
					{
						type: "and",
						queries: [],
					},
					{
						type: "or",
						queries: [
							{
								field: "ReportNumber",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "Format",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "Status",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "SubmittedDate",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "VerifiedDate",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "AbsContact",
								operation: "contains",
								value: searchStr,
							},
						],
					},
				],
			},
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
		};

		if (sortOptions) {
			requestPayload = { ...requestPayload, sortOptions };
		}

		setLoading(true);
		getQueryData(requestPayload)
			.then((response) => {
				setVesselRevisionData(response.data?.Data);
				setRowCount(response.data.Pagination.TotalItems);
				if (response.data.Data.length > 0) {
					hasRevisionData(true);
				}
				setLoading(false);
			})
			.catch((error) => {
				setVesselRevisionData([]);
				setLoading(false);
			});
	};

	const filterVessel = (
		columnFilterValue,
		pageIndex = 0,
		pageSize = 25,
		sortOptions
	) => {
		if (columnFilterValue) {
			const columnFilterPayload = columnFilterValue.map((element) => {
				let singleQuery = {
					field: element.id,
					operation: "contains",
					value: element.value,
				};

				return singleQuery;
			});

			let requestPayload = {
				entity: "SEEMPIMODTL",
				query: {
					conditions: [
						{
							type: "and",
							queries: [...columnFilterPayload],
						},
					],
				},
				pagination: {
					page: pageIndex + 1,
					pageSize: pageSize,
				},
			};

			if (sortOptions) {
				requestPayload = { ...requestPayload, sortOptions };
			}

			setLoading(true);
			getQueryData(requestPayload)
				.then((response) => {
					if (response.data.Data) {
						setVesselRevisionData(response.data.Data);
						if (response.data.Data.length > 0) {
							hasRevisionData(true);
						}
						setRowCount(response.data.Pagination.TotalItems);
					}
					setLoading(false);
				})
				.catch((error) => {
					setVesselRevisionData([]);
					setLoading(false);
					// emitToasterMsg(
					//     "error",
					//     "Vessel Revision",
					//     error.response.data.errorMessage
					// );
				});
		}
	};

	const vesselRevisionTableColumns = useMemo(
		() => [
			{
				header: "Report Number",
				enableHiding: false,
				accessorKey: "ReportNumber",
			},
			// {
			//     header: "Format",
			//     accessorKey: "Format",
			// },
			{
				header: "Report Format",
				accessorKey: "Format",
			},
			{
				header: "Status",
				accessorKey: "Status",
			},
			{
				header: "Submitted Date",
				enableHiding: false,
				accessorKey: "SubmittedDate",
				accessorFn: (row) => {
					if (row.SubmissionDate) {
						return convertLocaltoUTCFormat(row.SubmissionDate);
					}
				},
			},
			{
				header: "Verified Date",
				accessorKey: "VerifiedDate",
			},
			// {
			//     header: "Reviewer",
			//     accessorKey: "Reviewer",
			// },
			// {
			//     header: "Verifier",
			//     accessorKey: "Verifier",
			// },
			{
				header: "ABS Contact",
				accessorKey: "AbsContact",
			},
		],
		[]
	);

	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const seempTable = useMaterialReactTable({
		columns: vesselRevisionTableColumns,
		data: vesselRevisionData,
		enableRowSelection: true,
		enableStickyHeader: true,
		manualPagination: true,
		manualFiltering: true,
		manualSorting: true,
		rowCount,
		enableRowActions: true,
		positionActionsColumn: "last",
		displayColumnDefOptions: {
			"mrt-row-actions": {
				header: "",
			},
		},
		getRowId: (row) => row.VesselId,
		onRowSelectionChange: setVesselRevisionRowSelection,
		onPaginationChange: setVesselRevisionPagination,
		onGlobalFilterChange: setVesselRevisionGlobalFilter,
		onSortingChange: setVesselRevisionSort,
		onColumnFiltersChange: setVesselRevisionColumnFilters,
		state: {
			rowSelection: vesselRevisionRowSelection,
			pagination: vesselRevisionPagination,
			globalFilter: vesselRevisionGlobalFilter,
			columnFilters: vesselRevisionColumnFilters,
			sorting: vesselRevisionSort,
			isLoading: loading,
		},
		initialState: {
			density: "compact",
		},
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 379px)",
			},
		},
		renderRowActionMenuItems: ({ row, table }) => [
			<MRT_ActionMenuItem
				icon={<VisibilityOutlined />}
				key="view-report-details"
				label="View Report Details"
				onClick={() => console.info("View Report Details")}
				table={table}
			/>,
			<MRT_ActionMenuItem
				icon={<VisibilityOutlined />}
				key="continue-edit-report"
				label="Continue Edit Report"
				onClick={() => console.info("Continue Edit Report")}
				table={table}
			/>,
			<MRT_ActionMenuItem
				icon={<DescriptionOutlined />}
				key="delete-report"
				label="Delete Report"
				onClick={() => console.info("Delete Report")}
				table={table}
			/>,
			// <MRT_ActionMenuItem
			//     icon={<FileDownloadOutlined />}
			//     key="download"
			//     label="Download"
			//     onClick={() => console.info('Download')}
			//     table={table}
			// />,
		],
		renderToolbarInternalActions: ({ table }) => (
			<>
				<MRT_ToggleGlobalFilterButton
					id="vessel_view_global_search"
					table={table}
				/>
				<MRT_ShowHideColumnsButton
					id="vessel_view_show_hide_Columns"
					table={table}
				/>
				<MRT_ToggleFiltersButton
					id="vessel_view_global_filter"
					table={table}
				/>
			</>
		),
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
			},
		},
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
	});

	return (
		<div className="vessel-revision-table-wrapper">
			<MaterialReactTable table={seempTable} />
		</div>
	);
};

export default VesselRevisionGridTable;
